import { useState, useEffect, useLayoutEffect, useCallback, useMemo, useRef, useEffectEvent } from 'react';
import { useBackend } from '@/hooks/useBackend';
import { useSnackbar } from '@/hooks/useSnackbar';
import { useSelectOptions } from '@/lib/options/useSelectOptions';
import { OptionKey } from '@/lib/options/optionTypes';
import { log } from '@/lib/utils/logger';
import { RecipeTemplateDto, AvailableTemplate, RecipeDto, SampleDto, StatusDto } from '@/api/ApiClient';
import { RecipeState } from '@/components/recipe';
import { RecipeStore } from '@/components/recipe/stores';
import { mapRecipeDtoToState, createTemplateFromRecipe, mapTemplateToRecipeState } from '@/lib/types/recipeMapper';
import { enrichRecipeStateWithLookupValues } from '@/lib/utils/recipeUtils';
import { toRecipeTemplateStep } from '@/lib/utils/recipeTemplateStep';
import {
  getRecipeTemplateWithTechniqueFallback,
  primaryRecipeTemplateTechnique,
} from '@/lib/utils/recipeTemplateLookup';

const OPTION_KEYS: OptionKey[] = ['materials', 'synthesisTechniques', 'synthesisInstruments', 'bulkRawMaterials'];

/**
 * Configuration for recipe editor hook
 */
export interface RecipeEditorConfig {
  /** Current activity (for determining type, technique, instrument) */
  activity?: {
    type?: string;
    technique?: string;
    instrument?: string;
    recipe?: RecipeDto;
    recipeTemplate?: string;
    status?: StatusDto;
    sample?: SampleDto;
  };
  /** Whether the editor is in readonly mode */
  isReadOnly?: boolean;
  /** Whether to auto-load templates on mount */
  autoLoadTemplates?: boolean;
  /**
   * Called when the user edits raw recipe grid inputs in {@link RecipeStore} (MobX).
   * Use to sync parent dirty state (e.g. tree `setDataModified`)—cell edits do not flow through React state.
   */
  onRecipeInputChange?: () => void;
  /** Called when the recipe template calculates runtime from {@link RecipeTemplateDto.calcRuntimeFormula}. */
  onRuntimeCalculated?: (runtime: number) => void;
}

/**
 * Return type for useRecipeEditor hook
 */
export interface RecipeEditorState {
  // Recipe data
  recipeData: RecipeState | undefined;
  setRecipeData: (data: RecipeState | undefined) => void;

  // Template management
  fullRecipeTemplate: RecipeTemplateDto | null;
  setFullRecipeTemplate: (template: RecipeTemplateDto | null) => void;
  recipeTemplateOptions: AvailableTemplate[];
  loadingTemplates: boolean;
  loadRecipeTemplates: () => Promise<void>;

  // Recipe store
  recipeStore: RecipeStore | null;
  isInitializingRecipeStore: boolean;
  /** Force the store to be recreated with current recipeData and fullRecipeTemplate (e.g. after "Update Template" in modal). */
  requestRecreateStore: () => void;

  // Computed
  isReadOnly: boolean;
  hasRecipe: boolean;
  hasTemplate: boolean;
  canEdit: boolean;

  // Options
  bulkRawMaterials: any;

  // Computed template for Recipe component
  recipeTemplateDTO: RecipeTemplateDto | null;

  // Shared helpers for callers that need explicit template checks/updates.
  checkTemplateOutdated: (params: {
    activityType?: string;
    technique?: string;
    sampleTechnique?: string;
    instrument?: string;
    templateName?: string;
    storedHash?: string;
    availableTemplateNames?: string[];
    requireTemplateInOptions?: boolean;
  }) => Promise<boolean>;
  applyLatestTemplateUpdate: (params: {
    activityType?: string;
    technique?: string;
    sampleTechnique?: string;
    instrument?: string;
    templateName?: string;
    currentData?: RecipeState;
    sampleStartingMaterials?: SampleDto[];
  }) => Promise<{ success: boolean; latestTemplate?: RecipeTemplateDto; mergedRecipe?: RecipeState; reason?: string }>;
}

/**
 * Shared hook for recipe editing logic
 *
 * Handles:
 * - Recipe template loading
 * - Recipe data state management
 * - RecipeStore initialization
 * - Recipe enrichment with lookup values
 *
 * Can be used by both RecipeActivityDetail and BulkRecipeEditorDialog
 */
export function useRecipeEditor(config: RecipeEditorConfig = {}): RecipeEditorState {
  const {
    activity,
    isReadOnly: externalReadOnly,
    autoLoadTemplates = true,
    onRecipeInputChange,
    onRuntimeCalculated,
  } = config;
  const backend = useBackend();
  /** Latest `onRecipeInputChange` for RecipeStore without recreating the store when the callback identity changes. */
  const onRecipeInputChangeRef = useRef<(() => void) | undefined>(undefined);
  const onRuntimeCalculatedRef = useRef<((runtime: number) => void) | undefined>(undefined);
  useLayoutEffect(() => {
    onRecipeInputChangeRef.current = onRecipeInputChange;
  }, [onRecipeInputChange]);
  useLayoutEffect(() => {
    onRuntimeCalculatedRef.current = onRuntimeCalculated;
  }, [onRuntimeCalculated]);
  const { showSnackbar } = useSnackbar();
  const { options, loading: bulkMaterialsLoading } = useSelectOptions(OPTION_KEYS);

  // Extract all needed values from activity at the top - activity only changes when activityId prop changes
  // Never access activity directly in effects/callbacks - use these extracted values instead
  const recipe = activity?.recipe;
  // Use recipe.templateName if available, otherwise fall back to activity.recipeTemplate
  const recipeTemplateName = recipe?.templateName || activity?.recipeTemplate;
  const activityType = activity?.type;
  const activityTechnique = activity?.technique;
  const sampleTechniqueId = activity?.sample?.techniqueId;
  const activityInstrument = activity?.instrument;
  const activityStatus = activity?.status;
  const hasActivityRecipe = !!recipe;

  // For preparation activities, if activity doesn't have technique, fall back to sample's technique
  const effectiveTechnique = useMemo(() => {
    return activityTechnique ?? sampleTechniqueId;
  }, [activityTechnique, sampleTechniqueId]);

  // recipeData is derived from recipe - only changes when activityId prop changes
  const [recipeData, setRecipeData] = useState<RecipeState | undefined>(() => {
    if (recipe) {
      return mapRecipeDtoToState(recipe);
    }
    return undefined;
  });

  // Reset recipeData when activity.recipe changes (e.g. BulkRecipeEditorDialog async load).
  // Adjusting state during render when props change — avoids useEffect sync (cascading renders).
  const [trackedActivityRecipe, setTrackedActivityRecipe] = useState(recipe);
  if (recipe !== trackedActivityRecipe) {
    setTrackedActivityRecipe(recipe);
    if (recipe) {
      setRecipeData(mapRecipeDtoToState(recipe));
    } else if (!hasActivityRecipe) {
      setRecipeData(undefined);
    }
  }
  const [fullRecipeTemplate, setFullRecipeTemplate] = useState<RecipeTemplateDto | null>(null);
  const [recipeTemplateOptions, setRecipeTemplateOptions] = useState<AvailableTemplate[]>([]);
  const [loadingTemplates, setLoadingTemplates] = useState(false);
  const [recipeStore, setRecipeStore] = useState<RecipeStore | null>(null);
  const [isInitializingRecipeStore, setIsInitializingRecipeStore] = useState(false);
  /** Last recipeData used to build recipeStore — detect replace/re-init when reference changes. */
  const storeRecipeDataRef = useRef<RecipeState | undefined>(undefined);

  // Determine if readonly
  const isReadOnly = useMemo(() => {
    if (externalReadOnly !== undefined) return externalReadOnly;
    return activityStatus === StatusDto.Submitted;
  }, [externalReadOnly, activityStatus]);

  // Effect event for orchestration - accesses latest backend, showSnackbar, and recipe without needing them in deps
  const orchestrateRecipeEditor = useEffectEvent(
    async (
      currentRecipeTemplateName: string | undefined,
      currentActivityType: string | undefined,
      currentActivityTechnique: string | undefined,
      currentSampleTechniqueId: string | undefined,
      currentActivityInstrument: string | undefined,
      currentHasActivityRecipe: boolean,
      currentAutoLoadTemplates: boolean,
      controller: AbortController
    ) => {
      const primaryTechnique = primaryRecipeTemplateTechnique(
        currentActivityType,
        currentActivityTechnique,
        currentSampleTechniqueId,
        currentActivityInstrument
      );
      const templateLookupParams = {
        activityType: currentActivityType,
        activityTechnique: currentActivityTechnique,
        sampleTechniqueId: currentSampleTechniqueId,
        instrument: currentActivityInstrument,
      };

      try {
        // Clear state if we can't load templates
        if (!currentAutoLoadTemplates || !currentActivityType) {
          if (!currentAutoLoadTemplates) {
            setRecipeData(undefined);
            setFullRecipeTemplate(null);
            setRecipeTemplateOptions([]);
            setRecipeStore(null);
          }
          return;
        }

        // Clear state if we have no recipe template name and no activity recipe (will be set by orchestration)
        if (!currentRecipeTemplateName && !currentHasActivityRecipe) {
          setRecipeData(undefined);
          setFullRecipeTemplate(null);
          setRecipeTemplateOptions([]);
          setRecipeStore(null);
        }

        // Path 1: Existing recipe - sync recipeData and load full template
        // Process recipe if it exists, even without templateName (like RecipeActivityDetail does)
        if (recipe) {
          // Set recipe data from existing recipe (even if empty structure)
          const initialState = mapRecipeDtoToState(recipe);
          if (controller.signal.aborted) return;
          setRecipeData(initialState);

          // Load full template for existing recipe
          if (currentRecipeTemplateName) {
            // If templateName is available, load it directly
            try {
              const template = await getRecipeTemplateWithTechniqueFallback(backend, {
                ...templateLookupParams,
                templateName: currentRecipeTemplateName,
              });
              if (template && !controller.signal.aborted) {
                setFullRecipeTemplate(template);
              }
            } catch (err: any) {
              log.error('[useRecipeEditor] Failed to load full template:', err);
              // Don't show error - template might not exist yet
            }
          } else {
            // No templateName - try to find template by loading available templates
            // If exactly one matches, use it (like Knockout does)
            try {
              const step = toRecipeTemplateStep(currentActivityType) ?? null;
              const templates = await backend.recipeTemplate.availableTemplates(
                step,
                primaryTechnique,
                currentActivityInstrument || null
              );

              if (templates?.length === 1 && !controller.signal.aborted) {
                const templateName = templates[0]?.name;
                if (templateName) {
                  try {
                    const template = await getRecipeTemplateWithTechniqueFallback(backend, {
                      ...templateLookupParams,
                      templateName,
                    });
                    if (template && !controller.signal.aborted) {
                      setFullRecipeTemplate(template);
                    }
                  } catch (err: any) {
                    log.error('[useRecipeEditor] Failed to load template for existing recipe:', err);
                  }
                }
              }
            } catch (err: any) {
              log.error('[useRecipeEditor] Failed to load templates for existing recipe:', err);
              // Don't show error - recipe can still be displayed using derived template
            }
          }
          return;
        }

        // Path 2: No recipe - load templates, then auto-load if exactly one
        if (!currentHasActivityRecipe) {
          setLoadingTemplates(true);
          try {
            const step = toRecipeTemplateStep(currentActivityType) ?? null;
            const templates = await backend.recipeTemplate.availableTemplates(
              step,
              primaryTechnique,
              currentActivityInstrument || null
            );

            if (controller.signal.aborted) return;
            setRecipeTemplateOptions(templates || []);
            setLoadingTemplates(false);

            // If exactly one template, auto-load it and create empty recipe
            if (templates?.length === 1) {
              const templateName = templates[0]?.name;
              if (templateName) {
                try {
                  const template = await getRecipeTemplateWithTechniqueFallback(backend, {
                    ...templateLookupParams,
                    templateName,
                  });
                  if (template && !controller.signal.aborted) {
                    setFullRecipeTemplate(template);
                    // Create empty recipe from template
                    const emptyRecipe = mapTemplateToRecipeState(template, undefined);
                    setRecipeData(emptyRecipe);
                  }
                } catch (err: any) {
                  log.error('[useRecipeEditor] Failed to load template for empty recipe:', err);
                  // Don't show error - template might not exist yet
                }
              }
            }
          } catch (err: any) {
            if (!controller.signal.aborted) {
              log.error('[useRecipeEditor] Failed to load recipe templates:', err);
              showSnackbar('Failed to load recipe templates', 'error');
              setLoadingTemplates(false);
            }
          }
        }
      } catch (err: any) {
        if (!controller.signal.aborted) {
          log.error('[useRecipeEditor] Orchestration error:', err);
        }
      }
    }
  );

  // SINGLE ORCHESTRATION EFFECT: Chains all sequential async operations
  // Handles: recipe data sync, template loading (both existing and empty recipe cases)
  // Note: orchestrateRecipeEditor is useEffectEvent - never include in deps, it's stable and captures latest values
  // State clearing is handled inside orchestrateRecipeEditor to avoid state updates in useEffect
  useEffect(() => {
    const controller = new AbortController();
    const orchestrate = async () => {
      await orchestrateRecipeEditor(
        recipeTemplateName,
        activityType,
        activityTechnique,
        sampleTechniqueId,
        activityInstrument,
        hasActivityRecipe,
        autoLoadTemplates,
        controller
      );
    };
    void orchestrate();

    return () => {
      controller.abort();
    };
  }, [
    recipeTemplateName,
    autoLoadTemplates,
    activityType,
    activityTechnique,
    sampleTechniqueId,
    activityInstrument,
    hasActivityRecipe,
    effectiveTechnique,
  ]);

  // Load recipe templates (manual trigger - for when auto-load is disabled)
  const loadRecipeTemplates = useCallback(async () => {
    if (!backend || !activityType) {
      setRecipeTemplateOptions([]);
      return;
    }

    setLoadingTemplates(true);
    try {
      const primaryTech = primaryRecipeTemplateTechnique(
        activityType,
        activityTechnique,
        sampleTechniqueId,
        activityInstrument
      );
      const step = toRecipeTemplateStep(activityType) ?? null;
      const templates = await backend.recipeTemplate.availableTemplates(step, primaryTech, activityInstrument || null);
      setRecipeTemplateOptions(templates || []);
    } catch (err: any) {
      log.error('[useRecipeEditor] Failed to load recipe templates:', err);
      showSnackbar('Failed to load recipe templates', 'error');
    } finally {
      setLoadingTemplates(false);
    }
  }, [backend, activityType, activityTechnique, sampleTechniqueId, activityInstrument, showSnackbar]);

  // Effect event for RecipeStore initialization - accesses latest showSnackbar without needing it in deps
  const initializeRecipeStore = useEffectEvent(
    (currentRecipeData: RecipeState, currentTemplate: RecipeTemplateDto, currentBulkRawMaterials: any) => {
      // Wait for bulk materials if we have lookup columns that need them
      const hasLookups = currentRecipeData.groups.some((g) => g.columns.some((c) => c.isLookup && c.lookupValues));
      if (hasLookups && bulkMaterialsLoading) return;

      // Check if we need full template (for formula constants)
      const hasFormulaConstants = currentRecipeData.groups.some((group) =>
        group.columns.some((col) => col.source?.includes('const['))
      );
      if (hasFormulaConstants && !currentTemplate?.formulaConstants) return;

      setIsInitializingRecipeStore(true);
      try {
        // Dispose of existing store if recreating (e.g., when full template loads with constants)
        if (recipeStore) {
          recipeStore.dispose();
        }

        // Enrich recipe data with lookup values if needed and available
        let dataToUse = currentRecipeData;
        if (hasLookups && currentBulkRawMaterials && !bulkMaterialsLoading) {
          const alreadyEnriched = currentRecipeData.groups.some((g) =>
            g.columns.some((c) => c.isLookup && c.dropdownValues?.length > 0)
          );
          if (!alreadyEnriched) {
            const enriched = enrichRecipeStateWithLookupValues(currentRecipeData, currentBulkRawMaterials);
            if (enriched !== currentRecipeData) {
              dataToUse = enriched;
              setRecipeData(enriched);
            }
          }
        }

        // Create RecipeStore with (potentially enriched) recipe data; optional callbacks bridge MobX edits to parents.
        const store = new RecipeStore(
          dataToUse,
          currentTemplate,
          () => onRecipeInputChangeRef.current?.(),
          (runtime) => onRuntimeCalculatedRef.current?.(runtime)
        );
        setRecipeStore(store);
        storeRecipeDataRef.current = dataToUse;
      } catch (err: any) {
        log.error('[useRecipeEditor] Error initializing recipe store:', err);
        showSnackbar('Failed to initialize recipe editor', 'error');
      } finally {
        setIsInitializingRecipeStore(false);
      }
    }
  );

  // Convert RecipeDto to RecipeTemplateDto for the Recipe component
  // Always prefer fullRecipeTemplate if available (has formulaConstants)
  // Fallback: derive template structure from existing recipe (lacks formulaConstants)
  const recipeTemplateDTO = useMemo((): RecipeTemplateDto | null => {
    if (fullRecipeTemplate) {
      return fullRecipeTemplate;
    }
    // Use extracted values, never access activity directly
    if (recipe && activityType) {
      return createTemplateFromRecipe(recipe, activityType);
    }
    return null;
  }, [activityType, fullRecipeTemplate, recipe]);

  // SINGLE REACTIVE EFFECT: Creates RecipeStore once all dependencies are ready
  // Handles: enrichment with lookup values (if needed) + RecipeStore creation
  // Note: initializeRecipeStore is useEffectEvent - never include in deps, it's stable and captures latest values
  // Prefer fullRecipeTemplate when available; otherwise fall back to recipe-derived template so existing
  // recipes remain editable even when the server template cannot be loaded.
  // Note: Recreate store when fullRecipeTemplate loads (has constants needed for derived fields)
  useEffect(() => {
    if (!recipeData) return;

    const templateForStore = fullRecipeTemplate ?? recipeTemplateDTO;
    if (!templateForStore) return;

    // Check if we need to recreate the store:
    // 1. No store exists yet, OR
    // 2. Full template just loaded (has formulaConstants) but store was created with derived template (no constants), OR
    // 3. Template version changed (e.g. "Update Template" in modal) - store must use new template and recipe data
    const storeHasConstants = recipeStore?.getTemplate()?.formulaConstants != null;
    const templateHasConstants = templateForStore?.formulaConstants != null;
    const templateHashChanged =
      templateForStore?.hash != null && recipeStore?.getTemplate()?.hash !== templateForStore.hash;
    const recipeDataReplaced = recipeStore != null && recipeData != null && storeRecipeDataRef.current !== recipeData;
    const needsRecreate =
      !recipeStore || (templateHasConstants && !storeHasConstants) || templateHashChanged || recipeDataReplaced;

    if (needsRecreate) {
      const recreateStore = async () => {
        initializeRecipeStore(recipeData, templateForStore, options.bulkRawMaterials);
      };
      void recreateStore();
    }
  }, [recipeData, fullRecipeTemplate, recipeTemplateDTO, recipeStore, bulkMaterialsLoading, options.bulkRawMaterials]);

  // Treat store as active only when both recipe data and template are currently available.
  const activeRecipeStore = useMemo(() => {
    if (!recipeData) return null;
    if (!(fullRecipeTemplate ?? recipeTemplateDTO)) return null;
    return recipeStore;
  }, [recipeData, fullRecipeTemplate, recipeTemplateDTO, recipeStore]);

  // Computed values
  const hasRecipe = useMemo(() => !!recipeData, [recipeData]);
  const hasTemplate = useMemo(() => !!recipeTemplateDTO, [recipeTemplateDTO]);
  const canEdit = useMemo(
    () => !isReadOnly && hasRecipe && hasTemplate && !!activeRecipeStore,
    [isReadOnly, hasRecipe, hasTemplate, activeRecipeStore]
  );

  // Force store recreation with current recipeData and fullRecipeTemplate (e.g. after "Update Template").
  // Call after setRecipeData + setFullRecipeTemplate so the effect recreates with the new data.
  const requestRecreateStore = useCallback(() => {
    if (recipeStore) {
      recipeStore.dispose();
    }
    storeRecipeDataRef.current = undefined;
    setRecipeStore(null);
  }, [recipeStore]);

  const checkTemplateOutdated = useCallback(
    async ({
      activityType: checkActivityType,
      technique,
      sampleTechnique,
      instrument,
      templateName,
      storedHash,
      availableTemplateNames,
      requireTemplateInOptions = false,
    }: {
      activityType?: string;
      technique?: string;
      sampleTechnique?: string;
      instrument?: string;
      templateName?: string;
      storedHash?: string;
      availableTemplateNames?: string[];
      requireTemplateInOptions?: boolean;
    }): Promise<boolean> => {
      if (!backend || !checkActivityType || !templateName) return false;
      if (!storedHash) return true; // Legacy recipe with no hash should be considered outdated.
      if (requireTemplateInOptions && availableTemplateNames && !availableTemplateNames.includes(templateName)) {
        return false;
      }
      try {
        const currentTemplate = await getRecipeTemplateWithTechniqueFallback(backend, {
          activityType: checkActivityType,
          activityTechnique: technique,
          sampleTechniqueId: sampleTechnique,
          instrument,
          templateName,
        });
        return currentTemplate ? storedHash !== currentTemplate.hash : false;
      } catch (err: any) {
        log.error('[useRecipeEditor] Failed to check template version:', err);
        return false;
      }
    },
    [backend]
  );

  const applyLatestTemplateUpdate = useCallback(
    async ({
      activityType: updateActivityType,
      technique,
      sampleTechnique,
      instrument,
      templateName,
      currentData,
      sampleStartingMaterials,
    }: {
      activityType?: string;
      technique?: string;
      sampleTechnique?: string;
      instrument?: string;
      templateName?: string;
      currentData?: RecipeState;
      sampleStartingMaterials?: SampleDto[];
    }): Promise<{
      success: boolean;
      latestTemplate?: RecipeTemplateDto;
      mergedRecipe?: RecipeState;
      reason?: string;
    }> => {
      if (!backend || !updateActivityType || !templateName) {
        return { success: false, reason: 'missing-input' };
      }
      try {
        const latestTemplate = await getRecipeTemplateWithTechniqueFallback(backend, {
          activityType: updateActivityType,
          activityTechnique: technique,
          sampleTechniqueId: sampleTechnique,
          instrument,
          templateName,
        });
        if (!latestTemplate) {
          return { success: false, reason: 'template-not-found' };
        }

        const updatedRecipe = mapTemplateToRecipeState(latestTemplate, sampleStartingMaterials);
        const sourceData = currentData ?? recipeStore?.toState() ?? recipeData;
        const mergedRecipe: RecipeState = sourceData
          ? {
              ...updatedRecipe,
              templateName: latestTemplate.name || '',
              templateHash: latestTemplate.hash,
              groups: updatedRecipe.groups.map((newGroup) => {
                const existingGroup = sourceData.groups.find((g) => g.symbol === newGroup.symbol);
                return existingGroup ? { ...newGroup, rows: existingGroup.rows } : newGroup;
              }),
            }
          : {
              ...updatedRecipe,
              templateName: latestTemplate.name || '',
              templateHash: latestTemplate.hash,
            };

        setRecipeData(mergedRecipe);
        setFullRecipeTemplate(latestTemplate);
        if (recipeStore) {
          recipeStore.dispose();
        }
        setRecipeStore(null);
        return { success: true, latestTemplate, mergedRecipe };
      } catch (err: any) {
        log.error('[useRecipeEditor] Failed to apply latest template:', err);
        return { success: false, reason: 'update-failed' };
      }
    },
    [backend, recipeStore, recipeData]
  );

  return {
    recipeData,
    setRecipeData,
    fullRecipeTemplate,
    setFullRecipeTemplate,
    recipeTemplateOptions,
    loadingTemplates,
    loadRecipeTemplates,
    recipeStore: activeRecipeStore,
    isInitializingRecipeStore,
    requestRecreateStore,
    isReadOnly,
    hasRecipe,
    hasTemplate,
    canEdit,
    bulkRawMaterials: options.bulkRawMaterials,
    recipeTemplateDTO, // Computed template for Recipe component
    checkTemplateOutdated,
    applyLatestTemplateUpdate,
  };
}
